import { defineStore } from 'pinia';
import { ref, computed, watch, toRefs } from 'vue';
import { JoinOnRoadService as _joinOnRoadService } from '@/services/store/join-on-road.service';
import { JoinOnRoadDetails } from '@/interfaces/pages/join-on-road.interface';
import { CreatePartyResponse } from '@/interfaces/entities/create-party.interface';
import { JoinOnRoadProduct } from '@/interfaces/entities/jor-product.interface';
import { RoadsideAssistanceService as _roadsideAssistanceService } from '@/services/store/roadside-assistance.service';
import { PartyMatchStatus } from '@/interfaces/entities/roadside-assistance.interface';
import { dateToDateString } from '@/utils/data-transforms';
import { CreateQuoteResponse, SurchargeLevels } from '@/interfaces/entities/create-quote.interface';
import { IReviewSummary, IReviewSummaryItem, RACQAddressResult } from '@/interfaces';
import { FinaliseQuoteResponse } from '@/interfaces/entities/finalise-quote.interface';
import {
  CONFIRM_PURCHASE_STEP_ID,
  createRsaJobHtmlRequest,
  createRsaJobJoinOnRoadRequest,
  PAYMENT_CONFIRMATION_STEP_ID,
  PAYMENT_STEP_ID,
  REVIEW_STEP_ID,
  YOUR_DETAILS_PERSONAL_DETAILS_STEP_ID,
  YOUR_DETAILS_STEP_ID,
} from '@/forms/join-on-road/mappings/join-on-road-mappings';
import { calculateAge } from '@/utils/common-helper';
import { useAnalyticsStore } from '../analytics/analytics.state';
import {
  IAnalyticsForm,
  IAnalyticsPaymentDetail,
  IAnalyticsProfileInfo,
  IFormAnalytics,
} from '@/interfaces/forms/analytics-interface';
import { FormEvents } from '@/enums/form-events.enum';
import { useFormStore } from '../form/formStore';
import { useAccountStore } from '../account/account.state';
import { useFindCarStore } from '../find-car/find-car.state';
import { IsEligibleResponse } from '@/interfaces/entities/rsa-check-eligibility.interface';
import useVehicleEligibility from '@/composables/useVehicleEligibility';
import usePartyMatchState from '@/composables/usePartyMatchState';
import { mapToCreatePartyRequestForPersonal, mapToCreateQuoteRequest } from '@/mappers/roadside-assistance-mapper';
import { LoyaltyLevels } from '@/enums/loyalty-levels.enum';
import { CardTypes } from '@/enums/payment-types.enum';
import { GenericInvoiceEmailRequest } from '@/interfaces/entities/generic-invoice-email-request.interface';

export const useJoinOnRoadStore = defineStore('JoinOnRoad', () => {
  const analyticsStore = useAnalyticsStore();
  const formStore = useFormStore();
  const accountStore = useAccountStore();
  const findCarStore = useFindCarStore();

  // State
  const joinOnRoadDetails = ref<JoinOnRoadDetails>({
    breakdownLocationLatitude: 0,
    breakdownLocationLongitude: 0,
    breakdownLocationAddress: '',
    comments: '',
    vehicleRegistration: '',
    vehicleYear: '',
    vehicleMake: '',
    vehicleModel: '',
    vehicleColour: '',
    vehicleBody: '',
    isSearchByRego: false,
    phoneNumber: '',
    homeAddress: undefined,
    checkPostalAddress: false,
    postalAddress: undefined,
    phoneNumberToday: '',
    breakdownCause: '',
    title: '',
    firstName: '',
    lastName: '',
    emailAddress: '',
    birthDate: new Date(),
    jorSelectedProduct: undefined,
    finalProductPrice: 0,
    partyMatchStatus: undefined,
  });

  const { vehicleEligibility, setVehicleEligibility } = useVehicleEligibility();
  const { partyMatchState, setPartyMatchState } = usePartyMatchState();

  const joinOnRoadProducts = ref<JoinOnRoadProduct[]>([]);
  const baseProductCode = 'AR007'; /* base product is RACQ Everyday - AR007 */
  const nonMemberSurchargeProductCode = 'ARIS';
  const memberSurchargeProductCode = 'ARIS01';
  const skipYourDetailsPhoneAddressStep = ref<boolean | null>(null);
  const isPartyMatched = ref<boolean>(false);
  const showDiscountBanner = ref<boolean>(false);
  const showAgeDiscountBanner = ref<boolean>(false);
  const isPartyExitsOrCreated = ref<boolean>(false);
  const isQuoteCreated = ref<boolean>(false);
  const apttusId = ref<string | null>(null);
  const d365Id = ref<string | null>(null);
  const d365IdCreatedByJOR = ref<string | null>(null); // to avoid incorrect journey; ref. DS-3326
  const apttusCartId = ref<string | null>(null);
  const apttusQuoteId = ref<string | null>(null);
  const finalQuotePrice = ref<number | null>(null);
  const isQuoteFinalised = ref<boolean>(false);
  const assetId = ref<string | null>(null);
  const assetLineItemId = ref<string | null>(null);
  const prn = ref<string | null>(null);
  const invoiceId = ref<string | null>(null);
  const policyNumber = ref<string | null>(null);
  const isMemberLoggedIn = ref<boolean>(false);
  const membershipCardNumber = ref<string | null>(null);
  const isActiveMember = computed(() => eligibilityState.value?.isActiveMember);
  const surchargeProductPrice = computed(() =>
    getProductPrice(isActiveMember.value ? memberSurchargeProductCode : nonMemberSurchargeProductCode)
  );
  const surchargeProductMemberDiscount = computed(
    () => getProductPrice(nonMemberSurchargeProductCode) - getProductPrice(memberSurchargeProductCode)
  );
  const coreProductPrice = computed(() => getProductPrice(joinOnRoadDetails.value?.jorSelectedProduct));
  const totalProductPrice = computed(() => surchargeProductPrice.value + coreProductPrice.value);
  const isBaseProduct = computed(() => joinOnRoadDetails.value.jorSelectedProduct === baseProductCode);
  const productTitle = computed(() => getProductTitle(joinOnRoadDetails.value?.jorSelectedProduct));
  const isExistingParty = computed(() => isMemberLoggedIn.value || isPartyMatched.value);
  const isMemberWithActiveRsaProduct = computed(() => isExistingParty.value && eligibilityState.value?.isCovered);
  const eligibilityState = ref<IsEligibleResponse | null>(null);
  const ShouldJeopardyBasedOnPolicyStatus = ref<boolean | null>(null);
  const creditCardDetails = ref<Record<string, string>>({});
  const paymentReceipt = ref<{
    result: string;
    transactionId: string;
    reference: string;
  }>();
  const rsaTrackingId = ref<string | null>(null);
  const dispatchGroup = ref<string | null>(null);

  const createQuoteAttemptCount = ref<number>(0);
  const reviewSummaryItemEdit = ref<IReviewSummaryItem>();
  const reviewSummaryEdit = ref<IReviewSummary>();
  const shouldAdjustYourDetailsPhoneAddressStep = ref<boolean>(false); // Ref to track the adding/removing of yourdetails (with phone number and home/postal address fields) step
  const {
    vehicleRegistration,
    vehicleYear,
    vehicleMake,
    vehicleModel,
    vehicleColour,
    vehicleBody,
    firstName,
    lastName,
    emailAddress,
  } = toRefs(joinOnRoadDetails.value);
  const birthDate = computed(() => {
    const date = joinOnRoadDetails.value?.birthDate;
    return date ? dateToDateString(joinOnRoadDetails.value?.birthDate, 'ddMMyyyy') : '';
  });
  const isMissingResidentialAddress = ref(false);

  // Getters
  const getJoinOnRoadDetails = computed(() => joinOnRoadDetails.value);

  // Actions

  function setBreakdownLocation(latitude: number, longitude: number, formattedAddress: string) {
    joinOnRoadDetails.value.breakdownLocationLatitude = latitude;
    joinOnRoadDetails.value.breakdownLocationLongitude = longitude;
    joinOnRoadDetails.value.breakdownLocationAddress = formattedAddress;
  }

  function isBreakdownLocationSet() {
    return (
      joinOnRoadDetails.value.breakdownLocationLatitude !== 0 &&
      joinOnRoadDetails.value.breakdownLocationLongitude !== 0
    );
  }

  function setComments(comments: string) {
    joinOnRoadDetails.value.comments = comments;
  }

  function setReviewSummaryEditClicked(summary: IReviewSummary, summaryReviewItem: IReviewSummaryItem) {
    reviewSummaryEdit.value = summary;
    reviewSummaryItemEdit.value = summaryReviewItem;
  }

  function clearComments() {
    joinOnRoadDetails.value.comments = '';
  }

  function setVehicleDetails(
    vehicleRegistration: string,
    vehicleYear: string,
    vehicleMake: string,
    vehicleModel: string,
    vehicleColour: string,
    vehicleBody: string,
    isSearchByRego: boolean
  ) {
    joinOnRoadDetails.value.vehicleRegistration = vehicleRegistration;
    joinOnRoadDetails.value.vehicleYear = vehicleYear;
    joinOnRoadDetails.value.vehicleMake = vehicleMake;
    joinOnRoadDetails.value.vehicleModel = vehicleModel;
    joinOnRoadDetails.value.vehicleColour = vehicleColour;
    joinOnRoadDetails.value.vehicleBody = vehicleBody;
    joinOnRoadDetails.value.isSearchByRego = isSearchByRego;
  }

  function clearVehicleDetails() {
    joinOnRoadDetails.value.vehicleRegistration = '';
    joinOnRoadDetails.value.vehicleYear = '';
    joinOnRoadDetails.value.vehicleMake = '';
    joinOnRoadDetails.value.vehicleModel = '';
    joinOnRoadDetails.value.vehicleColour = '';
    joinOnRoadDetails.value.vehicleBody = '';
    joinOnRoadDetails.value.isSearchByRego = false;
  }

  async function setJORProducts(): Promise<boolean> {
    const jorProducts: JoinOnRoadProduct[] | null = await _joinOnRoadService.getJORProducts();
    if (jorProducts === null) {
      return false;
    }

    /*
    Set the type property based on productCode.
    ARIS, ARIS01 - Surcharge
    Rest of the ones -Product
    */

    const surchargeProductCodes = new Set([nonMemberSurchargeProductCode, memberSurchargeProductCode]);
    joinOnRoadProducts.value = jorProducts.map((jorProduct) => {
      return {
        ...jorProduct,
        type: surchargeProductCodes.has(jorProduct.productCode) ? 'surcharge' : 'product',
      };
    });

    return true;
  }

  function setDefaultJORProduct() {
    const defaultProductCode = baseProductCode;
    if (
      joinOnRoadProducts.value.length > 0 &&
      joinOnRoadProducts.value.some((jorProduct) => jorProduct.productCode === defaultProductCode)
    ) {
      joinOnRoadDetails.value.jorSelectedProduct = defaultProductCode;
    }
  }

  function updateJORProductSelection(productCode: string) {
    if (
      joinOnRoadProducts.value.length > 0 &&
      joinOnRoadProducts.value.some(
        (jorProduct) => jorProduct.productCode === productCode && jorProduct.type === 'product'
      )
    ) {
      joinOnRoadDetails.value.jorSelectedProduct = productCode;
    }
  }

  function getProductTitle(productCode: string | undefined): string {
    return joinOnRoadProducts.value?.find((jorProduct) => jorProduct.productCode === productCode)?.productName ?? '';
  }

  function getProductPrice(productCode: string | undefined): number {
    return joinOnRoadProducts.value?.find((jorProduct) => jorProduct.productCode === productCode)?.price ?? 0;
  }

  function setFinalProductPrice() {
    joinOnRoadDetails.value.finalProductPrice = totalProductPrice.value;
  }

  async function setPartyMatchStateJoinOnRoad(): Promise<boolean> {
    return await setPartyMatchState(
      joinOnRoadDetails.value?.firstName,
      joinOnRoadDetails.value?.lastName,
      dateToDateString(joinOnRoadDetails.value.birthDate, 'yyyy-MM-dd'),
      joinOnRoadDetails.value?.emailAddress
    );
  }

  function shouldShowDiscountBanner() {
    // hide discount banner if membership is not active
    if (!eligibilityState.value?.isActiveMember) return false;
    let showBanner = false;

    // RSA Products (lower case)
    const RACQ_EVERYDAY_PLUS = 'racq everyday plus';
    const RACQ_ULTRA_CARE = 'racq ultra care';
    const RACQ_ULTIMATE_CARE = 'racq ultimate care';

    const membershipLevel = eligibilityState.value?.membershipLoyaltyLevel.toLowerCase();
    const productTitleLower = productTitle.value.toLowerCase();

    switch (membershipLevel) {
      case LoyaltyLevels.Gold50:
        showBanner = true;
        break;
      case LoyaltyLevels.Gold:
        if ([RACQ_EVERYDAY_PLUS, RACQ_ULTRA_CARE, RACQ_ULTIMATE_CARE].indexOf(productTitleLower) > -1) {
          showBanner = true;
        }
        break;
      case LoyaltyLevels.Bronze:
      case LoyaltyLevels.Silver:
        if ([RACQ_EVERYDAY_PLUS, RACQ_ULTRA_CARE].indexOf(productTitleLower) > -1) {
          showBanner = true;
        }
        break;
    }

    return showBanner;
  }

  function shouldShowAgeDiscountBanner() {
    try {
      const minAge = 16;
      const maxAge = 21;
      const age = calculateAge(joinOnRoadDetails.value?.birthDate);
      return age >= minAge && age < maxAge;
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  async function checkAndSetRsaEligibility(): Promise<boolean> {
    try {
      // vehicleRegistration store value won't be available till the check is passed (get it from find car store)
      if (!d365Id.value || !findCarStore.registrationNumber) {
        console.error('Missing Vehicle Rego or d365Id');
        throw new Error('Missing Vehicle Rego or d365Id');
      }

      const response = await _roadsideAssistanceService.checkEligibility(
        findCarStore.registrationNumber,
        d365Id.value,
        null
      );
      eligibilityState.value = response;

      // Block user with active policy purchased today; ref. DS-3303
      if (response.surchargeRequired && !response.isCovered) {
        ShouldJeopardyBasedOnPolicyStatus.value = true;
      } else {
        ShouldJeopardyBasedOnPolicyStatus.value = await _roadsideAssistanceService.shouldJeopardy(
          eligibilityState.value?.policyStatus
        );
      }

      return response != null;
    } catch {
      return false;
    }
  }
  async function setJorDetailsFromIdentity(): Promise<boolean> {
    try {
      // Get identity details and prefill user details
      const identity = accountStore.identityDetails;

      // Fill in user details from identity
      if (identity?.birthDate) joinOnRoadDetails.value.birthDate = identity.birthDate;
      if (identity?.email) joinOnRoadDetails.value.emailAddress = identity.email;
      if (identity?.firstName) joinOnRoadDetails.value.firstName = identity.firstName;
      if (identity?.lastName) joinOnRoadDetails.value.lastName = identity.lastName;
      if (identity?.d365Id) d365Id.value = identity.d365Id;
      if (identity?.apttusId) apttusId.value = identity.apttusId;
      if (identity?.membershipCardNumber) membershipCardNumber.value = identity.membershipCardNumber;

      isMemberLoggedIn.value = !!identity?.d365Id;
      return true;
    } catch {
      return false;
    }
  }

  async function checkIsMissingResidentialAddress(d365Id?: string): Promise<void> {
    if (!d365Id) {
      isMissingResidentialAddress.value = false;
      return;
    }
    try {
      const res = await _roadsideAssistanceService.checkIsMissingResidentialAddress(d365Id);
      isMissingResidentialAddress.value = res ?? false;
    } catch {
      isMissingResidentialAddress.value = false;
    }
  }

  async function createParty() {
    if (!isPartyMatched.value && !isPartyExitsOrCreated.value) {
      const createPartyRequest = mapToCreatePartyRequestForPersonal(
        joinOnRoadDetails.value.title,
        joinOnRoadDetails.value?.firstName,
        joinOnRoadDetails.value?.lastName,
        dateToDateString(joinOnRoadDetails.value.birthDate, 'yyyy-MM-dd'),
        joinOnRoadDetails.value?.emailAddress,
        joinOnRoadDetails.value.phoneNumber,
        productTitle.value,
        joinOnRoadDetails.value.homeAddress as RACQAddressResult,
        joinOnRoadDetails.value.postalAddress as RACQAddressResult
      );

      const response: CreatePartyResponse | null = await _roadsideAssistanceService.createParty(createPartyRequest);
      if (response) {
        apttusId.value = response.apttusId;
        d365Id.value = response.d365Id;
        d365IdCreatedByJOR.value = response.d365Id;
        isPartyExitsOrCreated.value = true;
      }
    }
  }

  async function createQuote() {
    const maxCreateQuoteAttempts = 2;
    if (
      apttusId.value &&
      (isMemberLoggedIn.value || isPartyExitsOrCreated.value) &&
      !isQuoteCreated.value &&
      createQuoteAttemptCount.value < maxCreateQuoteAttempts
    ) {
      createQuoteAttemptCount.value += 1;

      const vehicleDetail = vehicleEligibility.value?.vehicleDetail;
      const createQuoteRequest = mapToCreateQuoteRequest(
        vehicleDetail,
        joinOnRoadDetails.value.jorSelectedProduct ?? '',
        productTitle.value,
        apttusId.value ?? '',
        vehicleDetail?.registrationNumber ?? joinOnRoadDetails.value.vehicleRegistration,
        vehicleDetail?.manufacturingYear ?? Number(joinOnRoadDetails.value.vehicleYear),
        vehicleDetail?.stateCode ?? joinOnRoadDetails.value.postalAddress?.state,
        vehicleDetail?.body.colour ?? joinOnRoadDetails.value.vehicleColour,
        vehicleDetail?.make.description ?? joinOnRoadDetails.value.vehicleMake,
        vehicleDetail?.model.description ?? joinOnRoadDetails.value.vehicleModel,
        isActiveMember.value ? SurchargeLevels.Member : SurchargeLevels.NonMember
      );

      const response: CreateQuoteResponse | null = await _roadsideAssistanceService.createQuote(createQuoteRequest);
      if (!response) {
        invalidateQuote();
      } else if (response.isError || !isPriceValid(response)) {
        // retry if quote contains error OR price incorrect
        invalidateQuote();
        await createQuote();
      } else if (!response.isError) {
        apttusCartId.value = response.apttusCartId;
        apttusQuoteId.value = response.apttusQuoteId;
        finalQuotePrice.value = response.price ?? null;
        createQuoteAttemptCount.value = 0;
        isQuoteCreated.value = true;
      }
    }
  }

  /**
   * Validate quote price
   *
   * @param {CreateQuoteResponse} response
   * @returns {boolean} validationResult
   */
  function isPriceValid(response: CreateQuoteResponse) {
    // Block $0 / partial $0 quote
    if (!response.price || surchargeProductPrice.value == response.price || coreProductPrice.value == response.price)
      return false;

    /**
     * Non active member and not within age discount should not have any discount
     * Match to quote price with digital price list
     */
    const digitalPriceMismatchApttusPrice = totalProductPrice.value != response.price;
    if (!eligibilityState.value?.isActiveMember && !showAgeDiscountBanner.value && digitalPriceMismatchApttusPrice) {
      return false;
    }

    return true;
  }

  async function finaliseQuote(): Promise<boolean> {
    try {
      if (apttusQuoteId.value !== null && apttusCartId.value !== null) {
        const response: FinaliseQuoteResponse | null = await _roadsideAssistanceService.finaliseQuote(
          apttusQuoteId.value ?? '',
          apttusCartId.value ?? ''
        );
        if (!response || response.isError) {
          invalidateQuoteFinalising();
          throw new Error('API_FAILED');
        } else if (!response.isError) {
          assetId.value = response.assetId;
          prn.value = response.prn;
          invoiceId.value = response.invoiceLineItem.invoiceId;
          isQuoteFinalised.value = true;
          assetLineItemId.value = response.invoiceLineItem.id;
          policyNumber.value = response.policyNumber;
        }
        return true;
      } else {
        throw new Error('MISSING_PARAMS');
      }
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  function invalidateParty() {
    if (!isMemberLoggedIn.value) {
      d365Id.value = null;
      apttusId.value = null;
    }
    isPartyMatched.value = isPartyExitsOrCreated.value = false;
  }

  function invalidateQuote() {
    apttusCartId.value = apttusQuoteId.value = finalQuotePrice.value = null;
    isQuoteCreated.value = false;
  }

  function invalidateQuoteFinalising() {
    assetId.value = prn.value = invoiceId.value = assetLineItemId.value = null;
    isQuoteFinalised.value = false;
  }

  const partyMatchStatusMap = {
    [PartyMatchStatus.Exact]: PartyMatchStatus.Exact,
    [PartyMatchStatus.None]: PartyMatchStatus.None,
    [PartyMatchStatus.Multiple]: PartyMatchStatus.Multiple,
  };

  watch(partyMatchState, (newVal) => {
    if (newVal) {
      invalidateParty();
      invalidateQuote();
      if (newVal !== null) {
        // Assign the party match status
        joinOnRoadDetails.value.partyMatchStatus =
          partyMatchStatusMap[newVal.matchStatus as PartyMatchStatus] || undefined;

        skipYourDetailsPhoneAddressStep.value = isPartyMatched.value = newVal.matchStatus === PartyMatchStatus.Exact;
        if (newVal.matchStatus === PartyMatchStatus.Exact) {
          apttusId.value = newVal?.apttusId ?? null;
          d365Id.value = newVal?.d365Id ?? null;
          isPartyExitsOrCreated.value = isPartyMatched.value = true;
        }
      } else {
        skipYourDetailsPhoneAddressStep.value = null;
      }
    }
  });

  watch(isPartyMatched, (newVal) => {
    if (newVal) {
      // When there is no logged in member session it is a must to keep the partyMatch status in sync with the account store
      // This will allow to show the banner text
      accountStore.partyMatched = newVal;
      accountStore.identityDetails = {
        firstName: joinOnRoadDetails.value.firstName,
      };
    } else {
      accountStore.partyMatched = false;
      accountStore.identityDetails = undefined;
    }
  });

  // Change in product selection - invalidate quote
  watch(productTitle, (newVal) => {
    if (newVal) {
      if (isMemberLoggedIn.value || isPartyMatched.value) {
        showDiscountBanner.value = shouldShowDiscountBanner();
      }
      invalidateQuote();
      createQuoteAttemptCount.value = 0;
    }
  });

  // Change in vehicle information - invalidate quote
  watch([vehicleRegistration, vehicleYear, vehicleMake, vehicleModel, vehicleModel], (newValues, oldValues) => {
    if (newValues.some((val, index) => val !== oldValues[index])) {
      invalidateQuote();
      createQuoteAttemptCount.value = 0;
    }
  });

  // Change in personal information - invalidate party and quote
  watch([firstName, lastName, emailAddress, birthDate], (newValues, oldValues) => {
    if (newValues.some((val, index) => val !== oldValues[index])) {
      shouldAdjustYourDetailsPhoneAddressStep.value = true;
      showAgeDiscountBanner.value = shouldShowAgeDiscountBanner();
      invalidateParty();
      invalidateQuote();
    }
  });

  const mapPaymentDetails = (formData: Record<string, any>) => {
    creditCardDetails.value = {
      cardToken: formData.cardToken,
      cardHolder: formData.cardHolder,
      maskedCardNumber: formData.MaskedCardNumber,
      cardExpiry: formData.cardExpiry,
      cardType: formData.cardType,
      autoRenew: formData.autoRenew,
    };
  };

  const submitPurchase = async () => {
    try {
      let result: any;

      if ([CardTypes.GooglePay, CardTypes.ApplePay].includes(creditCardDetails.value.cardType as CardTypes)) {
        const response = await _roadsideAssistanceService.submitPurchase({
          walletToken: creditCardDetails.value.cardToken,
          walletProvider: creditCardDetails.value.cardType,
          // Common fields
          prn: prn.value,
          amount: finalQuotePrice.value,
          invoiceId: invoiceId.value,
          apttusId: apttusId.value,
        });

        // Existing shape expected by callers
        result = response;
      } else {
        result = await _roadsideAssistanceService.submitPurchase({
          // Standard Fields
          cardToken: creditCardDetails.value.cardToken,
          autoRenew: creditCardDetails.value.autoRenew,
          prn: prn.value,
          amount: finalQuotePrice.value,
          invoiceId: invoiceId.value,
          apttusId: apttusId.value,
          // AutoRenew Exclusive Fields
          cardHolder: creditCardDetails.value.cardHolder,
          MaskedCardNumber: creditCardDetails.value.maskedCardNumber,
          cardExpiry: creditCardDetails.value.cardExpiry,
          cardType: creditCardDetails.value.cardType,
          assetId: assetId.value,
        });
      }

      paymentReceipt.value = result;

      return result;
    } catch (error) {
      return null;
    }
  };

  const createRsaJobJoinOnRoad = async (loyaltyLevel: string) => {
    try {
      const rsaRequest = await createRsaJobJoinOnRoadRequest(
        joinOnRoadDetails.value,
        apttusId.value,
        policyNumber.value,
        assetId.value,
        loyaltyLevel
      );

      const result = await _roadsideAssistanceService.createRsaJobJoinOnRoad(rsaRequest);

      if (result?.jobId) {
        // Set the trackingId if successful
        rsaTrackingId.value = result.jobId;
        dispatchGroup.value = result.roadsideAssistanceStatus?.dispatchGroup;
      }
      return result;
    } catch (error) {
      return null;
    }
  };

  const createRsaJobHtml = async () => {
    try {
      const rsaRequest = await createRsaJobHtmlRequest(joinOnRoadDetails.value);

      const result = await _roadsideAssistanceService.createRsaJobHtml(rsaRequest);

      if (result?.jobId) {
        // Set the trackingId if successful
        rsaTrackingId.value = result.jobId;
        dispatchGroup.value = result.roadsideAssistanceStatus?.dispatchGroup;
      }
      return result;
    } catch (error) {
      return null;
    }
  };

  const isUnderAge = (birthDate: Date): boolean | null => {
    try {
      const minAge = 16;
      return calculateAge(birthDate) < minAge;
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  const pushAnalytics = (options: {
    event: FormEvents;
    form: IAnalyticsForm;
    profileInfo?: IAnalyticsProfileInfo;
    payment?: Partial<IAnalyticsPaymentDetail>;
  }) => {
    const model: IFormAnalytics = {
      event: options.event,
      form: options.form,
    };

    // Set ProfileInfo
    model.profileInfo = {
      memberLevel: eligibilityState.value?.membershipLoyaltyLevel || 'Non-member',
      ...(d365Id.value && { contactID: d365Id.value }),
    } as IAnalyticsProfileInfo;

    if (options.payment) {
      model.payment = options.payment;
    }
    model.form.name = formStore.formName;

    analyticsStore.pushAnalytics(model);
  };

  const isTrackable = async () => {
    return await _roadsideAssistanceService.isTrackable(dispatchGroup.value!);
  };

  /**
   * Add/ remove steps
   */
  async function checkAndAdjustStep(
    stepId: string,
    addStep: boolean = false,
    indexToAddAfter: number | undefined = undefined
  ) {
    const step = formStore.getStep(stepId);
    if (step && !addStep) {
      await formStore.removeStep(step.stepIndex!);
    } else if (!step && addStep && indexToAddAfter) {
      formStore.addStepToScope(stepId, {
        removable: true,
        afterIndex: indexToAddAfter,
        withOrdinalNumber: false,
      });
    }
  }

  const adjustSteps = async () => {
    if (isMemberLoggedIn.value) {
      // remove your details step, your details phone address step
      await checkAndAdjustStep(YOUR_DETAILS_STEP_ID); // phone number, home address, postal address
      await checkAndAdjustStep(YOUR_DETAILS_PERSONAL_DETAILS_STEP_ID); // name, email, dob
    } else if (isPartyMatched.value) {
      // remove address step
      await checkAndAdjustStep(YOUR_DETAILS_STEP_ID); // phone number, home address, postal address
    } else {
      // add your details step, your details phone address step
      await checkAndAdjustStep(YOUR_DETAILS_STEP_ID, true, formStore.currentStepIndex + 1);
      await checkAndAdjustStep(YOUR_DETAILS_PERSONAL_DETAILS_STEP_ID, true, formStore.currentStepIndex + 2);
    }

    const reviewStepIndex = formStore.getStep(REVIEW_STEP_ID)?.stepIndex ?? -1;

    // skip steps after review step if the logged in member has an active RSA product
    if (isMemberWithActiveRsaProduct.value) {
      // Render as a 4 step form
      await checkAndAdjustStep(CONFIRM_PURCHASE_STEP_ID);
      await checkAndAdjustStep(PAYMENT_STEP_ID);
    } else if (reviewStepIndex > -1) {
      // Render as a 6 step form
      await checkAndAdjustStep(CONFIRM_PURCHASE_STEP_ID, true, reviewStepIndex + 1);
      await checkAndAdjustStep(PAYMENT_STEP_ID, true, reviewStepIndex + 2);
      await checkAndAdjustStep(PAYMENT_CONFIRMATION_STEP_ID, true, reviewStepIndex + 3);
    }
  };

  async function sendEmail(toAddress: string) {
    const vehicleDetails = joinOnRoadDetails.value;
    const colour = vehicleColour.value ?? vehicleDetails.vehicleColour ?? '';
    const vehicleString = `<p><strong>${vehicleDetails.vehicleMake} ${vehicleDetails.vehicleModel} ${vehicleDetails.vehicleYear}</strong></p><p>${vehicleDetails.vehicleBody} ${colour}</p>`;

    const emailRequest: GenericInvoiceEmailRequest = {
      receiptNumber: paymentReceipt.value?.reference.split('-')[0] ?? '',
      amount: finalQuotePrice.value!, // this is the total amount, inclusive of the immediate surcharge
      product: productTitle.value + '*',
      emailAddress: joinOnRoadDetails.value.emailAddress,
      firstName: joinOnRoadDetails.value.firstName,
      paymentMethod: creditCardDetails.value.cardType,
      phoneNumber: joinOnRoadDetails.value.phoneNumber,
      vehicle: vehicleString,
      d365Id: d365Id.value ?? undefined,
      toAddress: toAddress,
      isJoinOnRoad: true,
      joinOnRoadSurcharge: surchargeProductPrice.value,
    };

    await _roadsideAssistanceService.sendGenericInvoiceEmail(emailRequest);
  }

  return {
    joinOnRoadDetails,
    joinOnRoadProducts,
    baseProductCode,
    surchargeProductPrice,
    surchargeProductMemberDiscount,
    coreProductPrice,
    totalProductPrice,
    isBaseProduct,
    productTitle,
    partyMatchState,
    eligibilityState,
    ShouldJeopardyBasedOnPolicyStatus,
    creditCardDetails,
    skipYourDetailsPhoneAddressStep,
    isPartyMatched,
    showDiscountBanner,
    showAgeDiscountBanner,
    isMemberLoggedIn,
    isMemberWithActiveRsaProduct,
    isPartyExitsOrCreated,
    isQuoteCreated,
    apttusId,
    d365Id,
    d365IdCreatedByJOR,
    apttusCartId,
    isActiveMember,
    apttusQuoteId,
    finalQuotePrice,
    isQuoteFinalised,
    assetId,
    paymentReceipt,
    rsaTrackingId,
    dispatchGroup,
    assetLineItemId,
    membershipCardNumber,
    prn,
    invoiceId,
    policyNumber,
    shouldAdjustYourDetailsPhoneAddressStep,
    getJoinOnRoadDetails,
    reviewSummaryEdit,
    reviewSummaryItemEdit,
    vehicleEligibility,
    isMissingResidentialAddress,
    setBreakdownLocation,
    isBreakdownLocationSet,
    setComments,
    clearComments,
    setVehicleDetails,
    clearVehicleDetails,
    setJORProducts,
    setDefaultJORProduct,
    updateJORProductSelection,
    getProductPrice,
    getProductTitle,
    setFinalProductPrice,
    setPartyMatchStateJoinOnRoad,
    checkAndSetRsaEligibility,
    setJorDetailsFromIdentity,
    createQuote,
    finaliseQuote,
    mapPaymentDetails,
    submitPurchase,
    setVehicleEligibility,
    setReviewSummaryEditClicked,
    createRsaJobJoinOnRoad,
    createRsaJobHtml,
    isUnderAge,
    pushAnalytics,
    isTrackable,
    createParty,
    adjustSteps,
    checkIsMissingResidentialAddress,
    sendEmail,
  };
});
